import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useCloseUnusedRoom } from 'modules/Room/Hooks/useCloseUnusedRoom';

import { RoomStatusEnum } from 'shared/constants/shared-enums';
import { TIME_BEFORE_SHOW_TIMER_IN_MODALS } from 'shared/constants/timers';
import { Timer } from 'shared/constants/types';
import { useAccount } from 'shared/hooks/use-web3';
import { useRoomStore } from 'shared/store/roomStore';
import { epochToLocalUTC } from 'shared/utils/time';

interface UseTimerLogicProps {
  gameStatusName: string;
  setShowTimer: (value: boolean) => void;
  setShowCloseButton: (value: boolean) => void;
  setTechnicalDefeatError: (value: boolean) => void;
}

const TIME_BEFORE_DEADLINE_TO_SHOW = 4.5 * 60 * 1e3; // 4,5 minutes in milliseconds

export const useTimerLogic = ({
  gameStatusName,
  setShowTimer: setShowTimerParent,
  setShowCloseButton: setShowCloseButtonParent,
  setTechnicalDefeatError,
}: UseTimerLogicProps) => {
  const { closerRoom, roomStatus, roundCounter, setRoomExpire, setExpire } = useRoomStore();
  const [showInnerTimer, setShowInnerTimer] = useState(false);
  const timeOutRef = useRef<null | Timer>(null);
  const { activeRoomId } = useParams();
  const { address, chainId } = useAccount();

  const resetTimer = useCallback(() => {
    setShowInnerTimer(false);
    setShowTimerParent(false);
    setShowCloseButtonParent(false);
    if (timeOutRef.current) {
      clearTimeout(timeOutRef.current);
      timeOutRef.current = null;
    }
  }, []);

  const onExpire = useCallback(() => {
    setShowInnerTimer(false);
    setShowTimerParent(false);
    setShowCloseButtonParent(true);
    setExpire(true);
    setRoomExpire(true);
  }, [setShowTimerParent, setShowCloseButtonParent, setExpire, setRoomExpire]);

  useEffect(() => {
    resetTimer();
  }, [roomStatus, roundCounter, activeRoomId]);

  useEffect(() => {
    if (!closerRoom?.deadline || roomStatus === RoomStatusEnum.Closed) return;
    const deadlineDate = epochToLocalUTC(closerRoom?.deadline);
    const showTimerAt = new Date(deadlineDate.getTime() - TIME_BEFORE_SHOW_TIMER_IN_MODALS);

    const timerShouldBeVisibleAt = new Date(deadlineDate.getTime() - TIME_BEFORE_DEADLINE_TO_SHOW);
    const now = new Date();
    const diffInMilliseconds = timerShouldBeVisibleAt.getTime() - now.getTime();

    const timerKey = `${activeRoomId}${address}${chainId}${roundCounter}`;

    //if time to show the timer is already passed
    if (diffInMilliseconds <= 0) {
      setShowInnerTimer(true);
      setShowTimerParent(true);
    } else {
      // Set a timeout to show the timer
      timeOutRef.current = setTimeout(() => {
        setShowInnerTimer(true);
        setShowTimerParent(true);
      }, diffInMilliseconds);
    }

    return () => {
      clearTimeout(timeOutRef.current as Timer);
    };
  }, [
    closerRoom?.deadline,
    roomStatus,
    activeRoomId,
    address,
    chainId,
    roundCounter,
    setShowTimerParent,
  ]);

  const { closeUnusedRoomHandler, isSuccess, technicalDefeatError } = useCloseUnusedRoom();
  useEffect(() => {
    if (technicalDefeatError) setTechnicalDefeatError(technicalDefeatError);
  }, [technicalDefeatError, setTechnicalDefeatError]);

  return {
    isSuccess,
    showInnerTimer,
    setShowInnerTimer,
    closeUnusedRoomHandler,
    onExpire,
  };
};
